import get from 'lodash/get';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';

import { preRequisitesCheck } from '@components/common/formGroup/preRequisites';
import { checkConditions } from '@components/common/formGroup/conditionsEvaluator';
import type { ConditionsConfig } from '@components/common/formGroup/conditionsEvaluator';
import { ConfigValue, FormConfig, PreRequisites } from '@components/common/formSchema/types';
import { IDestinationStore } from '@stores/destination';
import { getSourceType } from '@components/common/util/util';
import { ConnectionMode, IDestDefinition } from '@stores/destinationDefsList';
import {
  SelectedSource,
  SelectedSourceDef,
} from '@components/workflows/steps/destinationSettings/util';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';

import {
  CollapsibleSection,
  ConnectionModeField,
  Field,
  DestUIConfigV2,
  FormElement,
  Group,
  NoteType,
  ConditionalNoteType,
  isMultiKeyField,
} from './types';

export const applyPrerequisitesCheck = <
  FormComponent extends { preRequisites?: PreRequisites; conditions?: ConditionsConfig },
>({
  formComponents,
  config,
  availableFeatureFlags = {},
  fieldDefaults = {},
}: {
  formComponents: FormComponent[];
  config: FormConfig;
  availableFeatureFlags?: Record<string, boolean>;
  fieldDefaults?: Record<string, unknown>;
}) =>
  formComponents.filter((formComponent) => {
    // Priority 1: Check new "conditions" field if present
    if (formComponent.conditions) {
      // Use NEW conditions system
      const { shouldShow } = checkConditions(formComponent.conditions, {
        formData: config,
        availableFeatureFlags,
        fieldDefaults,
      });
      return shouldShow;
    }

    // Priority 2: Fall back to old "preRequisites" system
    if (formComponent.preRequisites) {
      return preRequisitesCheck(formComponent.preRequisites, config, availableFeatureFlags);
    }

    // No conditions or preRequisites - always show
    return true;
  });

/**
 * Source-scoped fields live in form state under a flat key shaped
 * `<sourceType>-<configKey>`, while gates/conditions and the backend reference the
 * same value at the nested path `<configKey>.<sourceType>`. Returns that dotted path
 * so the value/default can be mirrored to it, or `null` when the key isn't
 * source-scoped.
 *
 * `connectionMode` is intentionally excluded — it has its own dedicated remap and
 * derived `connectionModes.*` settings handling.
 *
 * @param flatKey - A form-state config key shaped `<sourceType>-<configKey>`
 * @returns The dotted path `<configKey>.<sourceType>`, or `null` if not source-scoped
 */
const getSourceScopedDottedKey = (flatKey: string): string | null => {
  const separatorIndex = flatKey.indexOf('-');
  if (separatorIndex <= 0 || flatKey.endsWith('-connectionMode')) {
    return null;
  }

  const [sourceType, configKey] = flatKey.split('-');
  return `${configKey}.${sourceType}`;
};

/**
 * Builds a map of field default values from the UI template.
 * Traverses the template structure to extract configKey -> default value mappings.
 *
 * @param uiTemplate - The UI template structure containing collapsible sections
 * @returns Record mapping configKey to default value
 */
const buildFieldDefaultsMap = (uiTemplate: CollapsibleSection[]): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {};

  uiTemplate.forEach((collapsibleSection) => {
    collapsibleSection.sections.forEach((section) => {
      section.groups.forEach((group) => {
        group.fields.forEach((field) => {
          // Check if field has both configKey and default properties
          if ('configKey' in field && 'default' in field && field.default !== undefined) {
            defaults[field.configKey] = field.default;

            if (typeof field.configKey === 'string') {
              const dottedKey = getSourceScopedDottedKey(field.configKey);
              if (dottedKey) {
                defaults[dottedKey] = field.default;
              }
            }
          }
        });
      });
    });
  });

  return defaults;
};

export const recalculateUiTemplate = (
  uiTemplate: CollapsibleSection[],
  config: FormConfig,
  sources?: SelectedSource[],
  availableFeatureFlags: Record<string, boolean> = {},
) => {
  let updatedConfig = cloneDeep(config);

  // Build field defaults map from UI template for conditions evaluation
  const fieldDefaults = buildFieldDefaultsMap(uiTemplate);

  const sourceDefMap: Record<string, SelectedSourceDef> = sources
    ? keyBy(
        sources.map((source) => source.sourceDef),
        (sourceDef) => getSourceType(sourceDef),
      )
    : {};

  Object.keys(config).forEach((key) => {
    if (key.endsWith('-connectionMode')) {
      const [sourceType, sourceTypeKey] = key.split('-');
      const connectionModeValue = config[key] as ConnectionMode;
      set(updatedConfig, `${sourceTypeKey}.${sourceType}`, connectionModeValue);
      updatedConfig = getUpdatedConnectionModeSettingsConfig(
        updatedConfig,
        sourceType,
        connectionModeValue,
        sourceDefMap[sourceType]?.options?.isMobilePlatform,
      );
    } else {
      const dottedKey = getSourceScopedDottedKey(key);
      if (dottedKey) {
        set(updatedConfig, dottedKey, config[key]);
      }
    }
  });

  return uiTemplate.map((collapsibleSection) => {
    const collapsibleSectionCopy = cloneDeep(collapsibleSection);
    let filteredSections = applyPrerequisitesCheck({
      formComponents: collapsibleSectionCopy.sections,
      config: updatedConfig,
      availableFeatureFlags,
      fieldDefaults,
    });

    filteredSections.forEach((section) => {
      let filteredGroups = applyPrerequisitesCheck({
        formComponents: section.groups,
        config: updatedConfig,
        availableFeatureFlags,
        fieldDefaults,
      });

      filteredGroups.forEach((group) => {
        group.fields = applyPrerequisitesCheck({
          formComponents: group.fields,
          config: updatedConfig,
          availableFeatureFlags,
          fieldDefaults,
        });
        group.fields.forEach((field) => {
          if (typeof get(field, 'note.0.condition') === 'object') {
            const conditionalNotes: ConditionalNoteType = get(field, 'note');
            const finalNote = conditionalNotes.find((conditionalNote) =>
              preRequisitesCheck(conditionalNote.condition, updatedConfig),
            );
            if (finalNote) {
              set(field, 'note', finalNote.note);
            }
          }
        });
      });

      filteredGroups = filteredGroups.filter((group) => group.fields.length > 0);
      section.groups = filteredGroups;
    });

    filteredSections = filteredSections.filter((section) => section.groups.length > 0);
    collapsibleSectionCopy.sections = filteredSections;

    return collapsibleSectionCopy;
  });
};

const MOBILE_NOTE_LINK =
  'https://www.rudderstack.com/docs/destinations/streaming-destinations/amplitude/#adding-device-mode-integration';
const CONSENT_MANAGEMENT_FORM_GROUP_NOTE_LINK =
  'https://www.rudderstack.com/docs/sources/event-streams/sdks/consent-manager';

export const getSourceTypeForConnectionMode = (
  sourceDef: Pick<ISourceDefinition, 'name' | 'category'>,
) => {
  let sourceType = getSourceType(sourceDef);
  if (sourceType === 'cloud' || sourceType === 'warehouse') {
    sourceType = sourceDef.name.toLowerCase();
  }
  return sourceType;
};

export const getUniqueSourceMapByType = (
  selectedSources: SelectedSource[],
): Record<string, { displayName: string; type: string; sourceDef: SelectedSourceDef }> => {
  const uniqueSrcTypes: string[] = [];
  const uniqueSourceToDisplayNameAndTypeMap: Record<
    string,
    { displayName: string; type: string; sourceDef: SelectedSourceDef }
  > = {};
  selectedSources.forEach((source: SelectedSource) => {
    const uniqueSrc = getSourceTypeForConnectionMode(source.sourceDef);
    if (!uniqueSrcTypes.includes(uniqueSrc)) {
      uniqueSrcTypes.push(uniqueSrc);
      uniqueSourceToDisplayNameAndTypeMap[uniqueSrc] = {
        displayName: source.sourceDef.displayName,
        type: getSourceType(source.sourceDef),
        sourceDef: source.sourceDef,
      };
    }
  });
  return uniqueSourceToDisplayNameAndTypeMap;
};

export const getSourceGroupTitle = (sourceDef: SelectedSourceDef, displayName: string) => {
  let title = displayName;
  if (
    typeof sourceDef.options?.sdkExecutionEnvironment === 'string' &&
    sourceDef.options?.sdkExecutionEnvironment
  ) {
    title = `${title} SDK`;
  }

  return `${title} sources`;
};

const renameSourceDefinitionDisplayName = (
  selectedSources: SelectedSource[],
): Record<string, { displayName: string; sourceDef: SelectedSourceDef }> => {
  const sourceTypes: string[] = [];
  const sourceTypeToDisplayNameMap: Record<
    string,
    { displayName: string; sourceDef: SelectedSourceDef }
  > = {};
  selectedSources.forEach((source: SelectedSource) => {
    const sourceType = getSourceType(source.sourceDef);
    if (!sourceTypes.includes(sourceType)) {
      sourceTypes.push(sourceType);
      sourceTypeToDisplayNameMap[sourceType] = {
        displayName: source.sourceDef.displayName,
        sourceDef: source.sourceDef,
      };
      if (sourceType === 'cloud') {
        sourceTypeToDisplayNameMap[sourceType].displayName = 'Server-side SDKs and Cloud';
      } else if (sourceType === 'warehouse') {
        sourceTypeToDisplayNameMap[sourceType].displayName = 'Warehouse';
      }
    }
  });
  return sourceTypeToDisplayNameMap;
};

const getConnectionModeFields = (
  title: string,
  sourceType: string,
  destinationDefinition: IDestDefinition,
) => {
  const { supportedConnectionModes } = destinationDefinition.config;

  const sourceSupportedConnectionModes = supportedConnectionModes[sourceType];

  const connectionModeField: ConnectionModeField = {
    type: 'connectionMode',
    label: title,
    configKey: `${sourceType}-connectionMode`,
    value: 'cloud',
    sourceType,
    sourceSupportedConnectionModes,
    preRequisites: undefined,
  };
  return connectionModeField;
};

const getConfigTemplateFields = (
  configTemplate: Group | undefined,
  destDef: IDestDefinition,
  config: FormConfig,
  sourceType: string,
) => {
  const defaultFields: FormElement[] = [];
  const sourceSpecificFields: FormElement[] = [];

  if (!configTemplate) {
    // TODO: should be `return [[]]` — callers destructure `const [fields] = ...`,
    // so returning `[]` yields `fields = undefined` and crashes downstream `.length` reads.
    return [];
  }

  configTemplate.fields.forEach((field) => {
    if (isMultiKeyField(field)) {
      // MultiKey fields own multiple flat top-level keys; they aren't split by
      // source type and aren't keyed by a single configKey, so the
      // source-specific / default-field machinery doesn't apply.
      defaultFields.push(field);
      return;
    }
    const isFormField = field.type !== 'redirect';
    const isSourceSpecific =
      isFormField &&
      destDef.config.destConfig[sourceType] &&
      destDef.config.destConfig[sourceType].includes(field.configKey);
    const isDefault =
      isFormField &&
      destDef.config.destConfig.defaultConfig &&
      destDef.config.destConfig.defaultConfig.includes(field.configKey);

    if (isSourceSpecific) {
      const modifiedField = { ...field };
      modifiedField.configKey = `${sourceType}-${field.configKey}`;
      const value = config[field.configKey] as Record<string, ConfigValue>;

      if (value !== undefined && value[sourceType] !== undefined) {
        modifiedField.value = value[sourceType] as typeof modifiedField.value;
      }
      sourceSpecificFields.push(modifiedField);
    }

    if (isDefault) {
      const modifiedField = { ...field };
      const value = config[field.configKey] as ConfigValue;

      if (value !== undefined) {
        modifiedField.value = value as typeof modifiedField.value;
      }
      defaultFields.push(modifiedField);
    }

    if (field.type === 'redirect') {
      const modifiedField = { ...field };
      modifiedField.sourceType = sourceType;
      sourceSpecificFields.push(modifiedField);
    }
  });

  const allFields = defaultFields.concat(sourceSpecificFields);

  return [allFields];
};

const getFormGroupsFromConsentSettingsTemplate = (
  displayName: string,
  title: string,
  groupFields: FormElement[] = [],
): Group[] => {
  const uiFormGroups: Group[] = [];

  if (groupFields.length > 0) {
    const defaultNote = `Configure ${displayName}’s ${title} consent management settings.`;
    const note: NoteType = [
      `${defaultNote} Then, follow `,
      {
        text: 'relevant steps ',
        link: CONSENT_MANAGEMENT_FORM_GROUP_NOTE_LINK,
      },
      `to add the desired consent management provider integration to your application.`,
    ];

    uiFormGroups.push({
      title,
      note,
      fields: groupFields,
    });
  }

  return uiFormGroups;
};

const getFormGroupsFromSdkTemplate = (
  sdkGroupFields: FormElement[],
  displayName: string,
  title: string,
  sourceType: string,
  sourceDef: SelectedSourceDef,
): Group[] => {
  const uiFormGroups: Group[] = [];

  if (sdkGroupFields.length > 0) {
    const defaultNote = `Configure ${displayName}’s native ${title}.`;
    let note: NoteType;

    if (sourceDef.options?.isMobilePlatform === true) {
      note = [
        `${defaultNote} Then, follow `,
        {
          text: 'these steps ',
          link: MOBILE_NOTE_LINK,
        },
        `to add the ${displayName} integration to your project`,
      ];
    } else {
      note = defaultNote;
    }

    uiFormGroups.push({
      title,
      note,
      fields: sdkGroupFields,
      preRequisites: {
        fields: [
          {
            configKey: `${sourceType}-connectionMode`,
            value: 'device',
          },
          {
            configKey: `${sourceType}-connectionMode`,
            value: 'hybrid',
          },
        ],
        condition: 'or',
      },
    });
  }

  return uiFormGroups;
};

export const buildInitialUiTemplate = (
  formTemplate: DestUIConfigV2,
  destination: IDestinationStore,
  keysToRemove: Set<string>,
) => {
  const { baseTemplate, sdkTemplate, consentSettingsTemplate } = formTemplate;
  const uiTemplateCopy = cloneDeep(baseTemplate);
  const { config, destinationDefinition, sources, isRetlSourceConnected } = destination;
  const connectionModes: Field[] = [];
  const sdkGroups: Group[] = [];
  const consentSettingsGroups: Group[] = [];

  // Get connection mode fields config
  const uniqueSourceMapByType = getUniqueSourceMapByType(sources);
  Object.keys(uniqueSourceMapByType).forEach((sourceType) => {
    const title = getSourceGroupTitle(
      uniqueSourceMapByType[sourceType].sourceDef,
      uniqueSourceMapByType[sourceType].displayName,
    );

    const connectionModeField = getConnectionModeFields(
      title,
      uniqueSourceMapByType[sourceType].type,
      destinationDefinition,
    );
    connectionModes.push(connectionModeField);
  });

  // Get platform specific configurations
  const renamedSourceDisplayNameMap = renameSourceDefinitionDisplayName(sources);
  Object.keys(renamedSourceDisplayNameMap).forEach((sourceType) => {
    const title = getSourceGroupTitle(
      renamedSourceDisplayNameMap[sourceType].sourceDef,
      renamedSourceDisplayNameMap[sourceType].displayName,
    );

    // Get platform specific device/hybrid mode SDK fields config
    const [sdkGroupFields] = getConfigTemplateFields(
      sdkTemplate,
      destinationDefinition,
      config,
      sourceType,
    );
    Array.prototype.push.apply(
      sdkGroups,
      getFormGroupsFromSdkTemplate(
        sdkGroupFields,
        destinationDefinition.displayName,
        title,
        sourceType,
        renamedSourceDisplayNameMap[sourceType].sourceDef,
      ),
    );

    // Get platform specific consent management fields config
    const [consentSettingsGroupFields] = getConfigTemplateFields(
      consentSettingsTemplate,
      destinationDefinition,
      config,
      sourceType,
    );
    Array.prototype.push.apply(
      consentSettingsGroups,
      getFormGroupsFromConsentSettingsTemplate(
        destinationDefinition.displayName,
        title,
        consentSettingsGroupFields,
      ),
    );
  });

  // Add connection mode fields in the UI
  if (
    connectionModes.length > 0 &&
    destinationDefinition.category !== 'warehouse' &&
    !isRetlSourceConnected
  ) {
    uiTemplateCopy[0].sections[1].groups[0].fields = connectionModes;
  }

  // Add platform specific fields in UI
  const hasPlatformSpecificFields = sdkGroups.length > 0 || consentSettingsGroups.length > 0;
  if (hasPlatformSpecificFields) {
    uiTemplateCopy.forEach((collapsibleSection) => {
      if (collapsibleSection.title === 'Configuration settings') {
        collapsibleSection.sections.forEach((section) => {
          if (section.title === 'Destination settings') {
            // Add platform specific device/hybrid mode SDK fields in UI
            section.groups.push(...sdkGroups);
          } else if (section.id === 'consentSettings') {
            // Add platform specific consent management fields in UI
            section.groups.push(...consentSettingsGroups);
          }
        });
      }
    });
  }

  if (keysToRemove.size > 0) {
    uiTemplateCopy.forEach((collapsibleSection) => {
      collapsibleSection.sections.forEach((section) => {
        section.groups.forEach((group) => {
          group.fields = group.fields.filter((field) => {
            if (!('configKey' in field)) {
              return true;
            }
            return !keysToRemove.has(field.configKey);
          });
        });
      });
    });
  }

  return uiTemplateCopy;
};

export const transformFromBEtoFE = (destination: IDestinationStore) => {
  const { config, destinationDefinition, sources } = destination;
  const transformedConfig: FormConfig = {};

  destinationDefinition.config.destConfig.defaultConfig.forEach((key) => {
    if (config[key] !== undefined) {
      transformedConfig[key] = config[key];
    }
  });

  const selectedSourceTypes: string[] = [];
  sources.forEach((source) => {
    const sourceType = source.sourceDef.type;
    if (!selectedSourceTypes.includes(sourceType)) {
      selectedSourceTypes.push(sourceType);
    }
  });

  selectedSourceTypes.forEach((sourceType) => {
    if (destinationDefinition.config.destConfig[sourceType]) {
      destinationDefinition.config.destConfig[sourceType].forEach((key) => {
        const value = get(config, `${key}.${sourceType}`);
        if (value !== undefined) {
          transformedConfig[`${sourceType}-${key}`] = value;
        }
      });
    } else {
      transformedConfig[`${sourceType}-connectionMode`] = 'cloud';
    }
  });

  return transformedConfig;
};

export const getUpdatedConnectionModeSettingsConfig = (
  config: FormConfig,
  sourceType: string,
  connectionMode: string,
  isMobilePlatform?: boolean,
) => {
  const cloud = 'connectionModes.cloud';
  const updatedConfig = config;
  if (isMobilePlatform && connectionMode === 'device') {
    set(updatedConfig, 'connectionModes.mobileDevice', true);
  } else if (isMobilePlatform && connectionMode === 'hybrid') {
    set(updatedConfig, cloud, true);
    set(updatedConfig, 'connectionModes.mobileDevice', true);
  } else if (sourceType === 'web' && connectionMode === 'device') {
    set(updatedConfig, 'connectionModes.webDevice', true);
  } else if (sourceType === 'web' && connectionMode === 'hybrid') {
    set(updatedConfig, cloud, true);
    set(updatedConfig, 'connectionModes.webDevice', true);
  } else {
    set(updatedConfig, cloud, true);
  }

  return updatedConfig;
};

export const setFieldValue = (
  field: Field,
  config: FormConfig,
  secretConfig: FormConfig,
  defaultVal?: string,
) => {
  const updatedField: Field = { ...field };
  const configValue = get(config, updatedField.configKey);
  const secretConfigValue = get(secretConfig, updatedField.configKey);
  if (configValue !== undefined) {
    set(updatedField, 'value', configValue);
  } else if (
    updatedField.type === 'textInput' &&
    updatedField.secret &&
    secretConfigValue !== undefined
  ) {
    set(updatedField, 'placeholder', secretConfigValue);
  } else if (updatedField.value === undefined && defaultVal !== undefined) {
    updatedField.value = defaultVal;
  }
  return updatedField;
};
